import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Divider,
  IconButton,
  Avatar,
  Chip,
  Paper,
  Stack,
  Badge,
  Collapse,
  alpha,
} from "@mui/material";
import React, { useContext, useState, useEffect } from "react";
import axios from "axios";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { wpService, whatsAppFailureText } from "../services/wpService";
import {
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Flag as FlagIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";

// ---------- helper: format date/time ----------
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

// ---------- helper: ordinal suffix ----------
const ordinalSuffix = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// ---------- helper: readable status labels ----------
const formatStatus = (status) => {
  switch (status) {
    case "in_progress":
      return "In Progress";
    case "follow_up":
      return "Follow up";
    case "resolved":
      return "Resolved";
    case "recurring":
      return "Recurring";
    default:
      return status;
  }
};

// ---------- main component ----------
const FollowUpCard = ({
  ticket,
  onFollowUpSent = () => {},
  setSnackBarConfig = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { userInfo } = useContext(AutoTaskAuthContext) || {};

  // local follow‑up count
  const [localCount, setLocalCount] = useState(
    Number(ticket.follow_up_count || 0),
  );

  // ---------- ORIGINAL DIALOG STATES (unchanged) ----------
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [resolving, setResolving] = useState(false);

  // ---------- QUICK VIEW (expand/shrink) STATES ----------
  const [quickExpanded, setQuickExpanded] = useState(false);
  const [quickLogs, setQuickLogs] = useState([]);
  const [loadingQuickLogs, setLoadingQuickLogs] = useState(false);

  // whatsapp composer state
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState("");
  const [sending, setSending] = useState(false);

  // ---------- fetch logs for the FULL dialog ----------
  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/followup/logs/${ticket.id}`,
      );
      setLogs(res.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch follow-up logs:", error);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // ---------- fetch logs for the QUICK VIEW ----------
  const fetchQuickLogs = async () => {
    setLoadingQuickLogs(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/followup/logs/${ticket.id}`,
      );
      setQuickLogs(res.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch quick follow-up logs:", error);
      setQuickLogs([]);
    } finally {
      setLoadingQuickLogs(false);
    }
  };

  // ---- dialog open / close ----
  const handleOpenLogsDialog = () => {
    setLogsDialogOpen(true);
    fetchLogs(); // fetch only when dialog opens
  };

  const handleCloseLogsDialog = () => {
    if (sending || resolving) return;
    setLogsDialogOpen(false);
  };

  // ---- toggle quick view (does NOT open dialog) ----
  const handleToggleQuickView = (e) => {
    e.stopPropagation();
    if (!quickExpanded) {
      setQuickExpanded(true);
      fetchQuickLogs(); // fetch only when expanding
    } else {
      setQuickExpanded(false);
    }
  };

  // ---------- whatsapp (unchanged) ----------
  const handleOpenWhatsApp = (e) => {
    e.stopPropagation();
    const staffName = userInfo?.name || "Kumudu staff";
    const nextNumber = localCount + 1;
    const ordinal = ordinalSuffix(nextNumber);

    const autoMsg = `This is the ${ordinal} follow‑up from Kumudu.

${ticket.description || ""}

Best regards,
${staffName}`;

    setDraftMessage(autoMsg);
    setWhatsAppDialogOpen(true);
  };

  const handleCloseWhatsApp = () => {
    if (sending) return;
    setWhatsAppDialogOpen(false);
  };

  const handleSendWhatsApp = async () => {
    if (!draftMessage.trim()) {
      setSnackBarConfig({
        open: true,
        message: "Message cannot be empty",
        severity: "error",
      });
      return;
    }

    setSending(true);

    // The backend sends it to the store's group and logs it in one call.
    let result;
    try {
      result = await wpService.sendFollowUp({
        ticketId: ticket.id,
        message: draftMessage,
      });
    } catch (error) {
      // Nothing went out, so the dialog stays open with the draft for a retry.
      setSnackBarConfig({
        open: true,
        message: `Follow‑up not sent. ${error.message}`,
        severity: "error",
      });
      setSending(false);
      return;
    }

    if (result.followUpCount !== undefined) setLocalCount(result.followUpCount);
    else setLocalCount((prev) => prev + 1);

    setSnackBarConfig(
      result.logged
        ? {
            open: true,
            message: "Follow‑up sent successfully",
            severity: "success",
          }
        : {
            open: true,
            message:
              "Follow‑up sent on WhatsApp, but it couldn't be recorded on the ticket.",
            severity: "warning",
          },
    );
    onFollowUpSent();
    setWhatsAppDialogOpen(false);
    setSending(false);
  };

  // ---------- resolve (unchanged) ----------
  const handleResolve = async () => {
    setResolving(true);
    try {
      const payload = [
        {
          ticket_id: ticket.id,
          status: "resolved",
          time_taken: 10,
          time_budget: 10,
          pm_id: ticket.pm_id || null,
        },
      ];

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/ticket/updateticketsbulk`,
        payload,
      );

      if (response.data.statusCode === 200) {
        // The backend sends the store group its "Ticket Resolved" message and
        // reports here only when that failed.
        const whatsAppWarning = whatsAppFailureText(
          response.data.whatsapp?.[0],
        );
        setSnackBarConfig({
          open: true,
          message: whatsAppWarning || "Ticket resolved successfully",
          severity: whatsAppWarning ? "warning" : "success",
        });
        onFollowUpSent();
        setLogsDialogOpen(false); // close dialog on resolve
      } else {
        throw new Error(response.data?.statusMessage || "Resolution failed");
      }
    } catch (error) {
      console.error(error);
      setSnackBarConfig({
        open: true,
        message: error.message || "Failed to resolve ticket",
        severity: "error",
      });
    } finally {
      setResolving(false);
    }
  };

  // ---------- render dots ----------
  const MAX_DOTS = 5;
  const visibleDots = Math.min(localCount, MAX_DOTS);
  const overflow = localCount - MAX_DOTS;

  const renderDots = () => (
    <Stack direction="row" alignItems="center" spacing={isMobile ? 0.5 : 0.8}>
      {[...Array(MAX_DOTS)].map((_, i) => (
        <Box
          key={i}
          sx={{
            width: isMobile ? 7 : 10,
            height: isMobile ? 7 : 10,
            borderRadius: "50%",
            bgcolor: i < visibleDots ? theme.palette.primary.main : "#E8E8E8",
            transition: "all 0.3s",
            boxShadow:
              i < visibleDots
                ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`
                : "none",
          }}
        />
      ))}
      {overflow > 0 && (
        <Chip
          label={`+${overflow}`}
          size="small"
          sx={{
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 600,
            height: isMobile ? 16 : 20,
            "& .MuiChip-label": { px: 0.5, fontSize: isMobile ? 9 : 11 },
          }}
        />
      )}
    </Stack>
  );

  const canWhatsApp = ticket.type === "external";

  // 🔴 Red card condition: >= 5 follow‑ups AND still not resolved
  const isRedCard = localCount >= 5 && ticket.status !== "resolved";

  return (
    <>
      {/* ---------- MAIN CARD (click opens FULL dialog) ---------- */}
      <Paper
        elevation={0}
        onClick={handleOpenLogsDialog} // ✅ same as before
        sx={{
          p: isMobile ? 1.5 : 2.5,
          borderRadius: isMobile ? 3 : 4,
          border: `1px solid ${
            isRedCard ? "#ef9a9a" : alpha(theme.palette.divider, 0.15)
          }`,
          bgcolor: isRedCard ? "#FFF0F0" : "#ffffff",
          cursor: "pointer",
          transition: "all 0.25s",
          width: "100%",
          "&:hover": {
            boxShadow: isRedCard
              ? `0 6px 24px ${alpha("#ef5350", 0.15)}`
              : `0 6px 24px ${alpha(theme.palette.common.black, 0.06)}`,
            transform: "translateY(-2px)",
            borderColor: isRedCard ? "#ef5350" : theme.palette.primary.light,
          },
        }}
      >
        {/* header: store + ticket number + quick toggle */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={isMobile ? 1 : 2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={isMobile ? 0.5 : 1}
          >
            <StoreIcon
              fontSize="small"
              sx={{ color: isRedCard ? "#c62828" : theme.palette.primary.main }}
            />
            <Typography
              variant={isMobile ? "caption" : "body2"}
              fontWeight={600}
              color="text.primary"
              sx={{ wordBreak: "break-word" }}
            >
              {ticket.store_id} – {ticket.store_name}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={ticket.ticket_number}
              size="small"
              sx={{
                bgcolor: isRedCard ? "#c62828" : theme.palette.primary.main,
                color: "#fff",
                fontWeight: 600,
                borderRadius: 2,
                fontSize: isMobile ? 10 : 12,
                height: isMobile ? 22 : 28,
              }}
            />
            {/* ---------- QUICK VIEW TOGGLE (stopPropagation) ---------- */}
            <IconButton
              size="small"
              onClick={handleToggleQuickView}
              sx={{ color: isRedCard ? "#c62828" : "text.secondary" }}
            >
              {quickExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {/* description preview */}
        <Typography
          variant={isMobile ? "body2" : "body2"}
          sx={{
            mt: isMobile ? 1 : 1.5,
            color: "text.secondary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            fontSize: isMobile ? 13 : 14,
          }}
        >
          {ticket.description}
        </Typography>

        {/* meta chips */}
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={isMobile ? 0.5 : 1}
          sx={{ mt: isMobile ? 1 : 1.5 }}
        >
          <Chip
            size="small"
            label={ticket.department_name}
            sx={{
              bgcolor: "#E8ECF0",
              color: "#1A1A1A",
              fontWeight: 500,
              borderRadius: 2,
              fontSize: isMobile ? 10 : 12,
              height: isMobile ? 22 : 28,
            }}
          />
          <Chip
            size="small"
            label={ticket.priority}
            sx={{
              bgcolor:
                ticket.priority === "high"
                  ? "#FFE5E5"
                  : ticket.priority === "medium"
                  ? "#FFF3E0"
                  : "#E8F5E9",
              color:
                ticket.priority === "high"
                  ? "#B71C1C"
                  : ticket.priority === "medium"
                  ? "#E65100"
                  : "#1B5E20",
              fontWeight: 500,
              borderRadius: 2,
              fontSize: isMobile ? 10 : 12,
              height: isMobile ? 22 : 28,
            }}
          />
          <Chip
            size="small"
            label={formatStatus(ticket.status)}
            sx={{
              bgcolor:
                ticket.status === "in_progress"
                  ? "#E3F2FD"
                  : ticket.status === "follow_up"
                  ? "#FFF8E1"
                  : ticket.status === "resolved"
                  ? "#E8F5E9"
                  : "#E8ECF0",
              color:
                ticket.status === "in_progress"
                  ? "#0D47A1"
                  : ticket.status === "follow_up"
                  ? "#F57F17"
                  : ticket.status === "resolved"
                  ? "#1B5E20"
                  : "#1A1A1A",
              fontWeight: 500,
              borderRadius: 2,
              fontSize: isMobile ? 10 : 12,
              height: isMobile ? 22 : 28,
            }}
          />
        </Stack>

        {/* bottom row: follow‑up info + whatsapp */}
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "stretch" : "center"}
          spacing={isMobile ? 1 : 0}
          sx={{
            mt: isMobile ? 1.5 : 2,
            pt: isMobile ? 1 : 1.5,
            borderTop: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={isMobile ? 1 : 1.5}
          >
            <Badge
              badgeContent={localCount}
              color="primary"
              showZero
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: isMobile ? 10 : 11,
                  fontWeight: 700,
                  bgcolor: isRedCard ? "#c62828" : theme.palette.primary.main,
                  color: "#fff",
                },
              }}
            >
              <HistoryIcon
                fontSize="small"
                sx={{
                  color: isRedCard ? "#c62828" : theme.palette.primary.main,
                }}
              />
            </Badge>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{ fontSize: isMobile ? 10 : 12 }}
            >
              Follow‑ups
            </Typography>
            {renderDots()}
          </Stack>

          {canWhatsApp ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<WhatsAppIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
              onClick={handleOpenWhatsApp}
              sx={{
                bgcolor: "#25D366",
                borderRadius: 30,
                textTransform: "none",
                fontWeight: 600,
                px: isMobile ? 1.5 : 2,
                py: isMobile ? 0.5 : 0.8,
                fontSize: isMobile ? 11 : 13,
                "&:hover": { bgcolor: "#1DA851" },
                color: "#fff",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Follow up
            </Button>
          ) : (
            <Typography
              variant="caption"
              color="text.disabled"
              fontStyle="italic"
              sx={{ fontSize: isMobile ? 10 : 12 }}
            >
              Internal ticket
            </Typography>
          )}
        </Stack>

        {/* ---------- QUICK VIEW (collapsible timeline, does NOT open dialog) ---------- */}
        <Collapse in={quickExpanded} timeout={300} unmountOnExit>
          <Divider sx={{ mt: 2, mb: 1.5 }} />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 1, fontSize: isMobile ? 13 : 14 }}
          >
            Quick Follow‑up Timeline
          </Typography>

          {loadingQuickLogs ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          ) : quickLogs.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 1, textAlign: "center", fontSize: isMobile ? 12 : 14 }}
            >
              No follow‑ups yet.
            </Typography>
          ) : (
            <Stack spacing={0.8}>
              {quickLogs.map((log, index) => {
                const seq = quickLogs.length - index;
                return (
                  <Box
                    key={log.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      borderRadius: 2,
                      borderLeft: `3px solid ${
                        isRedCard ? "#ef5350" : theme.palette.primary.main
                      }`,
                    }}
                  >
                    <Chip
                      label={`#${seq}`}
                      size="small"
                      sx={{
                        bgcolor: isRedCard
                          ? "#ef5350"
                          : theme.palette.primary.main,
                        color: "#fff",
                        height: 18,
                        "& .MuiChip-label": { fontSize: 9, px: 1 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ flex: 1, fontSize: isMobile ? 10 : 12 }}
                    >
                      {formatDateTime(log.sent_at)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? 9 : 11 }}
                    >
                      {log.sent_by_name || "Unknown"}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Collapse>
      </Paper>

      {/* ---------- ORIGINAL LOGS + RESOLVE DIALOG (unchanged) ---------- */}
      <Dialog
        open={logsDialogOpen}
        onClose={handleCloseLogsDialog}
        fullWidth
        maxWidth={isMobile ? "xl" : "sm"}
        BackdropProps={{
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.15),
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 3 : 5,
            maxHeight: "90vh",
            bgcolor: "#ffffff",
            boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.08)}`,
            margin: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{ pb: 0.5, px: isMobile ? 1.5 : 3, pt: isMobile ? 1.5 : 2 }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              fontWeight={700}
              color="text.primary"
            >
              Follow Up Details
            </Typography>
            <IconButton
              onClick={handleCloseLogsDialog}
              disabled={sending || resolving}
              size={isMobile ? "small" : "medium"}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Stack>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: isMobile ? 11 : 13 }}
          >
            {ticket.ticket_number} • {formatDateTime(ticket.created_at)}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2, px: isMobile ? 1.5 : 3 }}>
          {/* ticket summary card */}
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 1.5 : 2,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
              mb: 2,
            }}
          >
            <Stack spacing={1}>
              <Typography
                variant={isMobile ? "body2" : "body1"}
                fontWeight={600}
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {ticket.description}
              </Typography>
              <Stack direction="row" flexWrap="wrap" spacing={isMobile ? 1 : 2}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <StoreIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isMobile ? 11 : 13 }}
                  >
                    {ticket.store_id} – {ticket.store_name}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <FlagIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      textTransform: "capitalize",
                      fontSize: isMobile ? 11 : 13,
                    }}
                  >
                    {ticket.priority}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isMobile ? 11 : 13 }}
                  >
                    {formatDateTime(ticket.created_at)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          <Divider sx={{ my: 2 }} />

          {/* full follow‑up history */}
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 1.5, fontSize: isMobile ? 13 : 14 }}
          >
            Follow‑up History
          </Typography>

          {loadingLogs ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress
                size={isMobile ? 24 : 30}
                sx={{ color: theme.palette.primary.main }}
              />
            </Box>
          ) : logs.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2, textAlign: "center", fontSize: isMobile ? 12 : 14 }}
            >
              No follow‑ups recorded yet.
            </Typography>
          ) : (
            <Stack
              spacing={isMobile ? 1 : 1.5}
              sx={{
                maxHeight: 280,
                overflowY: "auto",
                pr: 0.5,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
              }}
            >
              {logs.map((log, index) => {
                const seq = logs.length - index;
                return (
                  <Paper
                    key={log.id}
                    elevation={0}
                    sx={{
                      p: isMobile ? 1 : 1.5,
                      bgcolor: "#FAFAFC",
                      borderRadius: 3,
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 1px 4px ${alpha(
                        theme.palette.common.black,
                        0.04,
                      )}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={isMobile ? 0.5 : 1}
                      >
                        <Avatar
                          sx={{
                            width: isMobile ? 22 : 28,
                            height: isMobile ? 22 : 28,
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                          }}
                        >
                          <PersonIcon fontSize={isMobile ? "small" : "small"} />
                        </Avatar>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: isMobile ? 11 : 13 }}
                        >
                          {log.sent_by_name || "Unknown"}
                        </Typography>
                        <Chip
                          label={`#${seq}`}
                          size="small"
                          sx={{
                            bgcolor: theme.palette.primary.light,
                            color: "#fff",
                            height: isMobile ? 18 : 20,
                            "& .MuiChip-label": {
                              fontSize: isMobile ? 9 : 10,
                              px: 1,
                            },
                          }}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? 9 : 11 }}
                      >
                        {formatDateTime(log.sent_at)}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        ml: isMobile ? 3 : 4,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "text.primary",
                        bgcolor: alpha(theme.palette.grey[100], 0.4),
                        p: 1,
                        borderRadius: 2,
                        fontSize: isMobile ? 11 : 13,
                      }}
                    >
                      {log.message}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          )}

          {/* resolve button (only if not resolved) */}
          {ticket.status !== "resolved" && (
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={
                  resolving ? (
                    <CircularProgress
                      size={isMobile ? 16 : 20}
                      color="inherit"
                    />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                onClick={handleResolve}
                disabled={resolving}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: "#fff",
                  px: isMobile ? 2 : 4,
                  py: isMobile ? 0.8 : 1.2,
                  borderRadius: 30,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: isMobile ? 12 : 14,
                  boxShadow: `0 4px 16px ${alpha(
                    theme.palette.primary.main,
                    0.25,
                  )}`,
                  width: isMobile ? "100%" : "auto",
                  "&:hover": {
                    boxShadow: `0 6px 24px ${alpha(
                      theme.palette.primary.main,
                      0.35,
                    )}`,
                    transform: "scale(1.02)",
                  },
                  transition: "all 0.2s",
                }}
              >
                {resolving ? "Resolving..." : "Resolve (10 mins)"}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- WHATSAPP COMPOSER DIALOG (unchanged) ---------- */}
      <Dialog
        open={whatsAppDialogOpen}
        onClose={handleCloseWhatsApp}
        fullWidth
        maxWidth={isMobile ? "xl" : "sm"}
        BackdropProps={{
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.15),
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 3 : 4,
            bgcolor: "#ffffff",
            boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.08)}`,
            margin: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ px: isMobile ? 1.5 : 3, pt: isMobile ? 1.5 : 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              fontWeight={700}
            >
              Send WhatsApp Follow‑up
            </Typography>
            <IconButton
              onClick={handleCloseWhatsApp}
              disabled={sending}
              size={isMobile ? "small" : "medium"}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: isMobile ? 1.5 : 3 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontSize: isMobile ? 12 : 14 }}
          >
            To{" "}
            <strong style={{ color: theme.palette.primary.main }}>
              {ticket.store_id} – {ticket.store_name}
            </strong>
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={isMobile ? 4 : 6}
            value={draftMessage}
            onChange={(e) => setDraftMessage(e.target.value)}
            placeholder="Type your follow‑up message here..."
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: alpha(theme.palette.grey[100], 0.4),
                borderRadius: 3,
                fontSize: isMobile ? 13 : 14,
                "& fieldset": { borderColor: theme.palette.divider },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.light,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />

          <Stack
            direction={isMobile ? "column-reverse" : "row"}
            justifyContent="flex-end"
            spacing={isMobile ? 1 : 2}
            sx={{ mt: isMobile ? 2 : 3 }}
          >
            <Button
              onClick={handleCloseWhatsApp}
              disabled={sending}
              variant="text"
              sx={{
                color: theme.palette.text.secondary,
                width: isMobile ? "100%" : "auto",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendWhatsApp}
              disabled={sending || !draftMessage.trim()}
              sx={{
                bgcolor: "#25D366",
                borderRadius: 30,
                textTransform: "none",
                fontWeight: 600,
                px: isMobile ? 2 : 3,
                py: isMobile ? 0.8 : 1,
                fontSize: isMobile ? 12 : 14,
                "&:hover": { bgcolor: "#1DA851" },
                "&.Mui-disabled": { bgcolor: alpha("#25D366", 0.4) },
                color: "#fff",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {sending ? "Sending..." : "Send"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FollowUpCard;
